import { clsx } from 'clsx';
import { useMobile } from 'common-nextjs';
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import Image from '~/components/Image';
import { useModals } from '~/contexts/ModalsContext';
import { EditIcon, RemoveIcon } from '~/public/static/svg/ItemActionIcons';
import { ListFlowImage, deletePhoto } from '~/store/listFlow/slices/photos';
import { useAppDispatch } from '~/store/store';

interface Props {
  index: number;
  onMove: (oldIndex: number, newIndex: number) => void;
  photo: ListFlowImage;
}

interface DragItem {
  index: number;
}

const DraggablePhoto: React.FC<Props> = ({ index, photo, onMove }) => {
  const mobile = useMobile();
  const modals = useModals();
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  // const [hoverIndex, setHoverIndex] = useState(-1);

  const [{ handlerId }, drop] = useDrop<any, any, any>({
    accept: 'img',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem) {
      // setHoverIndex(item.index);
    },
    drop(item: DragItem) {
      onMove(item.index, index);
    },
  });

  const [{ isDragging, offset }, drag, preview] = useDrag({
    type: 'img',
    item: (): DragItem => ({ index }),
    collect: monitor => ({
      offset: monitor.getClientOffset(),
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const handleEditClick = () =>
    modals.openModal('photoEditModal', {
      index,
      photo,
      modalWidth: 600,
    });

  const handleDeleteClick = () => dispatch(deletePhoto({ index }));

  return (
    <div>
      {mobile && isDragging && (
        <Image
          src={photo.url}
          className="pointer-events-none fixed z-40 rounded object-cover"
          style={{
            top:
              (offset?.y ?? 0) - ((imageRef?.current?.height ?? 0) / 2 || 64),
            left:
              (offset?.x ?? 0) - ((imageRef?.current?.width ?? 0) / 2 || 64),
            height: imageRef?.current?.height,
            width: imageRef?.current?.width,
          }}
        />
      )}
      <div
        ref={ref}
        className={clsx(
          'bottom-gradient relative hover:cursor-move',
          isDragging && 'opacity-25',
        )}
      >
        <div className="aspect-w-1 aspect-h-1">
          <Image
            src={photo.url}
            className="rounded object-cover"
            ref={imageRef}
          />
        </div>

        <div className="absolute bottom-0 left-0 right-0 flex h-10 items-center justify-end space-x-4 rounded-b px-2 text-white">
          <button className="relative z-10" onClick={handleEditClick}>
            <EditIcon fill="currentColor" />
          </button>
          <button className="relative z-10" onClick={handleDeleteClick}>
            <RemoveIcon fill="currentColor" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DraggablePhoto;
